import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import styles from "@/components/forms/inputs/objectListInput.module.css";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import AnchorDataForm, {
  AnchorField,
  TypeMapping,
} from "@/components/anchorData/anchorDataForm";
import classNames from "classnames";

type GenericObject = Record<string, string>;

type LabelOption = {
  value: string;
  label: string;
};

type ModalMode = "edit" | "add";

type ObjectListInputProps = {
  name: string;
  label: string | any;
  value?: GenericObject[];
  onChange: (value: GenericObject[]) => void;
  labelOptions: LabelOption[] | string[];
  typeMapping?: TypeMapping | any;
  tableCellClassname?: any;
  isValueInherited?: boolean;
  editable?: boolean;
};

const toRows = (value: unknown): GenericObject[] =>
  Array.isArray(value) ? value : []; 

const ObjectListInput: FC<ObjectListInputProps> = ({
  value,
  label,
  // name,
  onChange,
  labelOptions,
  typeMapping,
  tableCellClassname,
  isValueInherited,
  editable,
}) => {
  const [rows, setRows] = useState<GenericObject[]>(toRows(value));
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const { t } = useTranslation();
  const modalTitle = modalMode === "add" ? t("add_new_element") : t("edit");
  const modalSubmitButtonText = modalMode === "add" ? t("add") : t("save");

  useEffect(() => {
    setRows(toRows(value));
  }, [value]);

  const keys = rows.length > 0 ? Object.keys(rows[0]) : [];

  const modalForm = useForm<any>({
    defaultValues: {},
  });

  const buildFieldsFromOptions = (): AnchorField[] => {
    return (labelOptions || []).map((opt: any) => {
      const fieldType = typeMapping[opt.value];
      return {
        name: opt.value,
        label: opt.label,
        type: fieldType,
        value: modalForm.getValues(opt.value) ?? "",
        isValueInherited: isValueInherited,
        editable: editable,
      };
    });
  };

  const labelMap: Record<string, string> = Object.fromEntries(
    labelOptions.map((opt: any) => [opt.value, opt.label]),
  );

  const openAddModal = () => {
    modalForm.reset({});
    setModalMode("add");
    setEditIndex(null);
    setModalOpen(true);
  };
  const openEditModal = (index: number) => {
    setModalMode("edit");
    setEditIndex(index);
    modalForm.reset(rows[index]);
    setModalOpen(true);
  };

  const deleteObjectListEntry = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
    onChange(updated);
  };

  const closeModal = () => {
    modalForm.reset({});
    setModalOpen(false);
  };

  const cancelClose = useModalDismiss(modalOpen, closeModal);

  const handleModalSubmit = (data: GenericObject) => {
    const updated = [...rows];
    if (modalMode === "edit" && editIndex !== null) {
      updated[editIndex] = data;
    } else {
      updated.push(data);
    }
    setRows(updated);
    onChange(updated);
    setModalOpen(false);
  };

  const modalContent = (
    <FormProvider {...modalForm}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          modalForm.handleSubmit(handleModalSubmit)(e as any);
        }}
      >
        <DialogTitle textAlign="center">{modalTitle}</DialogTitle>
        <DialogContent sx={{ overflowY: "visible" }}>
          <AnchorDataForm fields={buildFieldsFromOptions()} mode="modal" />
        </DialogContent>
        <DialogActions sx={{ p: "0.75rem", justifyContent: "space-between" }}>
          <GenericButton variant="outlined" onClick={closeModal}>
            {t("cancel")}
          </GenericButton>

          <GenericButton variant="contained" type="submit">
            {modalSubmitButtonText}
          </GenericButton>
        </DialogActions>
      </form>
    </FormProvider>
  );

  return (
    <>
      <section className={styles.labelWrapper}>
        <Typography variant="subtitle1">{label}</Typography>
        <GenericButton
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={openAddModal}
          size="small"
        >
          {t("add")}
        </GenericButton>
      </section>

      {rows.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {t("no_entries")}
        </Typography>
      ) : (
        <TableContainer component={Paper} className={styles.tableContainer}>
          <Table size="small" className={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell className={styles.tableCell}>
                  {t("actions")}
                </TableCell>
                {keys.map((key) => (
                  <TableCell key={key} className={styles.tableCell}>
                    {labelMap[key] || key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={rowIndex} hover>
                  <TableCell className={styles.tableActionButtonCell}>
                    <GenericButton
                      variant="icon"
                      color="iconBase"
                      onClick={() => {
                        openEditModal(rowIndex);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </GenericButton>
                    <GenericButton
                      variant="icon"
                      color="error"
                      onClick={() => deleteObjectListEntry(rowIndex)}
                    >
                      <DeleteIcon fontSize="small" />
                    </GenericButton>
                  </TableCell>
                  {keys.map((key) => (
                    <TableCell
                      key={key}
                      className={classNames({
                        [tableCellClassname]: tableCellClassname,
                      })}
                    >
                      {row[key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog
        open={modalOpen}
        {...cancelClose}
        PaperProps={{ sx: { maxHeight: "95%" } }}
        className={styles.dialogContainer}
        sx={{
          "& .MuiPaper-root": {
            maxWidth: "80vw",
            minWidth: "50vw",
          },
        }}
      >
        {modalContent}
      </Dialog>
    </>
  );
};

export default ObjectListInput;
